import { auth } from '@api/db'
import { FormBuilder, PhoneInput } from '@components'
import { Divider, ErrorText, FormStatePicker, FormZipcodeInput, HeaderText, Text } from '@elements'
import { FormikProps, getIn } from 'formik'
import { StyleSheet } from 'react-native'
import { Input } from 'react-native-elements'

import { useDeviceSize } from '../../../hooks/useLayout'
import { CheckoutFormType } from './helpers'

/** Form for entering the billing address in checkout */
export function CheckoutContact({
  formikProps: { handleChange, values, errors, touched, handleBlur, setFieldError },
}: {
  formikProps: FormikProps<CheckoutFormType>
}) {
  const { isSmallDevice } = useDeviceSize()
  return (
    <FormBuilder style={styles.container}>
      <Divider clear large />
      <HeaderText>Contact Information</HeaderText>
      <Text style={styles.marginV5}>
        Your phone number will be used by farmers to contact you for any last minute changes or important information.
      </Text>
      <Divider clear />
      <PhoneInput
        editable={auth().getProvider() !== 'phone'}
        value={values.billingAddress.phoneNumber}
        maskHandler={handleChange('billingAddress.phoneNumber')}
        onBlur={handleBlur('billingAddress.phoneNumber')}
        onChange={() => setFieldError('billingAddress.phoneNumber', undefined)}
        label={!isSmallDevice}
        errorMessage={getIn(errors, 'billingAddress.phoneNumber')}
        renderErrorMessage={getIn(touched, 'billingAddress.phoneNumber')}
      />
      <Divider clear />
      <HeaderText style={styles.marginV10}>Billing Address</HeaderText>
      <Input
        onChangeText={handleChange('billingAddress.street1')}
        // This must manually set the field error to undefined so the message can be cleared because the form settings are set to not validate on change or blur
        onChange={() => setFieldError('billingAddress.street1', undefined)}
        onBlur={handleBlur('billingAddress.street1')}
        value={values.billingAddress.street1}
        placeholder="Street Address 1"
        errorMessage={getIn(touched, 'billingAddress.street1') ? getIn(errors, 'billingAddress.street1') : ''}
      />
      <Input
        onChangeText={handleChange('billingAddress.street2')}
        // This must manually set the field error to undefined so the message can be cleared because the form settings are set to not validate on change or blur
        onChange={() => setFieldError('billingAddress.street2', undefined)}
        onBlur={handleBlur('billingAddress.street2')}
        value={values.billingAddress.street2}
        placeholder="Street Address 2"
        errorMessage={getIn(touched, 'billingAddress.street2') ? getIn(errors, 'billingAddress.street2') : ''}
      />
      <FormBuilder row>
        <Input
          onChangeText={handleChange('billingAddress.city')}
          // This must manually set the field error to undefined so the message can be cleared because the form settings are set to not validate on change or blur
          onChange={() => setFieldError('billingAddress.city', undefined)}
          onBlur={handleBlur('billingAddress.city')}
          value={values.billingAddress.city}
          placeholder="City"
          errorMessage={getIn(touched, 'billingAddress.street1') ? getIn(errors, 'billingAddress.city') : ''}
        />
        <FormStatePicker
          country={values.billingAddress.country}
          errorMessage={getIn(touched, 'billingAddress.state') ? getIn(errors, 'billingAddress.state') : ''}
          value={values.billingAddress.state}
          onValueChange={(e) => {
            handleChange('billingAddress.state')(e)
            // This must manually set the field error to undefined so the message can be cleared because the form settings are set to not validate on change or blur
            setFieldError('billingAddress.zipcode', undefined)
          }}
          onBlur={handleBlur('billingAddress.state')}
        />
        <FormZipcodeInput
          country={values.billingAddress.country}
          value={values.billingAddress.zipcode}
          onChangeText={handleChange('billingAddress.zipcode')}
          // This must manually set the field error to undefined so the message can be cleared because the form settings are set to not validate on change or blur
          onChange={() => setFieldError('billingAddress.zipcode', undefined)}
          onBlur={handleBlur('billingAddress.zipcode')}
          errorMessage={getIn(touched, 'billingAddress.zipcode') ? getIn(errors, 'billingAddress.zipcode') : ''}
        />
        {getIn(errors, 'billingAddress.updateError') ? (
          <ErrorText>{getIn(errors, 'billingAddress.updateError')}</ErrorText>
        ) : null}
      </FormBuilder>
    </FormBuilder>
  )
}

const styles = StyleSheet.create({
  marginV10: { marginVertical: 10 },
  marginV5: { marginVertical: 5 },
  container: {
    marginHorizontal: 20,
    marginVertical: 10,
  },
})
